'use client';

import { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';

import { Dialog, GreenCheck } from '~/shared';
import type { Advertiser } from '~/types';

import { StyledAdvertisersForm } from '../components/elements';
import { useUpdateAdvertiser } from '../hooks';

export type UpdateAdvertiserProps = {
  open: boolean;
  onClose?: () => void;
  onCloseTransitionEnded?: () => void;
  selectedAdvertiser: Advertiser | null;
  refetchList: () => void;
};

export function UpdateAdvertiserDialog({
  open,
  selectedAdvertiser,
  onClose,
  onCloseTransitionEnded,
  refetchList,
}: UpdateAdvertiserProps) {
  const [successSubmit, setSuccessSubmit] = useState(false);
  const [advertiserName, setAdvertiserName] = useState(
    selectedAdvertiser?.name ?? ''
  );
  const useUpdateAdvertiserMutation = useUpdateAdvertiser(() => {
    refetchList();
    setSuccessSubmit(true);
  });

  function handleClose() {
    if (useUpdateAdvertiserMutation.isPending) return;
    setAdvertiserName('');
    onClose?.();
    useUpdateAdvertiserMutation.reset();
    setTimeout(() => setSuccessSubmit(false), 300);
  }

  function handleSubmit() {
    if (advertiserName === '' || !selectedAdvertiser) return;
    useUpdateAdvertiserMutation.mutate({
      id: selectedAdvertiser.id,
      name: advertiserName,
    });
  }

  const dialogTitle = successSubmit
    ? 'Anunciante editado com sucesso'
    : `Editar Anunciante ${selectedAdvertiser?.name}`;

  const dialogIcon = successSubmit ? <GreenCheck size={70} /> : undefined;

  return (
    <Dialog
      title={dialogTitle}
      withIcon={dialogIcon}
      open={open}
      onClose={handleClose}
      onCloseTransitionEnded={() => {
        setSuccessSubmit(false);
        onCloseTransitionEnded?.();
      }}
    >
      {!successSubmit && (
        <StyledAdvertisersForm>
          <TextField
            id="advertiser-name"
            label="Nome do anunciante"
            fullWidth
            autoComplete="off"
            type="text"
            variant="filled"
            value={advertiserName}
            onChange={e => setAdvertiserName(e.target.value)}
            error={!!useUpdateAdvertiserMutation.error}
            disabled={useUpdateAdvertiserMutation.isPending}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              sx={{ px: 3, py: 1 }}
              size="small"
              fullWidth
              variant="outlined"
              onClick={() => handleClose()}
              disabled={useUpdateAdvertiserMutation.isPending}
            >
              Voltar
            </Button>
            <Button
              disabled={
                !advertiserName ||
                selectedAdvertiser?.name === advertiserName ||
                useUpdateAdvertiserMutation.isPending
              }
              sx={{ px: 3, py: 1 }}
              size="small"
              fullWidth
              variant="contained"
              type="submit"
              onClick={e => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              Salvar Alteração
            </Button>
          </Box>
          {useUpdateAdvertiserMutation.isError && (
            <Typography
              sx={{
                mt: 2,
                fontSize: theme => theme.typography.pxToRem(16),
                color: theme => theme.palette.error.light,
              }}
            >
              {useUpdateAdvertiserMutation.error?.message}
            </Typography>
          )}
        </StyledAdvertisersForm>
      )}
      {successSubmit && (
        <Button
          variant="outlined"
          size="small"
          sx={{ py: 2 }}
          fullWidth
          onClick={() => handleClose()}
        >
          Ver Anunciantes
        </Button>
      )}
    </Dialog>
  );
}
